/**
 * Amazing Network — shared by the mobile app and the web runner app.
 *
 * The API shapes (/api/network/*), the Orbit colours, and `layoutOrbit`, the
 * pure geometry both clients draw from. No React here: each app renders the
 * layout its own way (react-native-svg on mobile, plain SVG on web).
 */

/** A runner in the Amazing Network. Identified by runner_id — the API never sends emails here. */
export interface NetworkNode {
  id: string;
  name: string;
  city: string | null;
  /** e.g. "Season 9". */
  memberSince: string | null;
  picture: string | null;
  /** How many people this runner brought in (absent on search results). */
  childCount?: number;
  /** Search in recruit mode only: who already brought them in, if anyone. */
  referredBy?: { id: string; name: string } | null;
  /** In views: the family this runner belongs to, if any (an opaque id). */
  familyId?: string | null;
  /** Search in family mode only: whether they're in your family, another, or none. */
  inFamily?: 'yours' | 'other' | null;
}

/** /api/network/me, /node, /referrer and /recruits — the Orbit centred on one runner. */
export interface NetworkView {
  /** Null when the signed-in account has no runner profile (a coach-only login). */
  me: NetworkNode | null;
  /** The centre's family, the centre excluded. */
  family: NetworkNode[];
  /** Referrer chain, nearest first. */
  ancestors: NetworkNode[];
  children: NetworkNode[];
  grandchildren: (NetworkNode & { parentId: string })[];
  /** People three or more generations below, drawn only as a count. */
  moreCount: number;
  stats: { broughtIn: number; branchSize: number; clubTotal: number };
  /**
   * Who brought you in, typed as a name because they aren't in RHWB. Only on
   * your own view; null (or absent, from an older API) otherwise.
   */
  outsideReferrer?: string | null;
  /**
   * Said "I found RHWB myself" (and has no referrer). Only on your own view;
   * false (or absent, from an older API) otherwise.
   */
  foundSelf?: boolean;
  /** No referrer, no outside name, and never said "I found RHWB myself". */
  needsPrompt: boolean;
}

/**
 * The Orbit colours say where someone sits relative to the centre: blue is
 * the runner being looked at, purple who brought them, grey further up the
 * chain, teal everyone they brought in.
 */
export const ORBIT_COLORS = {
  center: '#1877F2',
  referrer: '#7C3AED',
  ancestor: '#9CA3AF',
  child: '#14B8A6',
  grandchild: '#5EC9B8',
  overflow: '#E6F7F4',
  overflowText: '#0F766E',
  /** Someone outside RHWB, drawn as a grey outline. */
  outside: '#9CA3AF',
  family: '#D98A1C',
  familyFill: '#FDF1DC',
  familyStroke: '#F2C57C',
  ring: '#D5D9DF',
} as const;

/** "Season 9" → "S9"; anything else passes through. */
export const shortSeason = (memberSince: string | null | undefined): string | null => {
  if (!memberSince) return null;
  const m = /season\s*(\d+)/i.exec(memberSince);
  return m ? `S${m[1]}` : memberSince;
};

export const firstName = (name: string | null | undefined): string =>
  name?.trim().split(/\s+/)[0] ?? '';

/** "City · since S9" — tells same-name runners apart without an email. */
export const nodeMeta = (node: Pick<NetworkNode, 'city' | 'memberSince'>): string =>
  [node.city, shortSeason(node.memberSince) && `since ${shortSeason(node.memberSince)}`]
    .filter(Boolean)
    .join(' · ');

// ── Orbit layout ───────────────────────────────────────────────────────────

/**
 * Where every node of the Amazing Network "Orbit" goes. Pure geometry, no
 * React, so it runs under `node --test` and both apps draw from it.
 *
 *   ancestors      stacked straight up, the referrer sitting on top of the outer ring
 *                  (or, with no referrer: someone outside RHWB, "found it myself",
 *                  or the "?" slot)
 *   you            the centre, with your family beside you in a pill-shaped bubble
 *   inner ring     the people you brought in
 *   outer ring     the people *they* brought in, each fanned inside its
 *                  parent's share of the ring
 *
 * Some angles are kept empty (wedges): the top one when a line goes up (a
 * referrer or the "?" slot), and one on each side when your family pill is
 * there. The rest of the circle — the free arcs — is shared out evenly, so a
 * ring's nodes never sit on a wedge. When a ring (or one parent's share of the
 * outer ring) has more people than fit without touching, the last visible spot
 * becomes a "+N" chip. On your own view the last inner spot is a dashed "+".
 *
 * Family: neighbours on a ring who share a family are drawn next to each
 * other, with an arc bubble behind them.
 */

export interface OrbitInput {
  /** Referrer chain, nearest first. */
  ancestorIds: string[];
  childIds: string[];
  grandchildren: { id: string; parentId: string }[];
  /** Draw the dashed "?" where a referrer would go. */
  showReferrerSlot: boolean;
  /**
   * With no ancestors: draw the referrer spot as someone outside RHWB (id
   * "outside"), linked by a dashed line. Takes the place of the "?".
   */
  outsideReferrer?: boolean;
  /**
   * With no ancestors and no outside referrer: draw the referrer spot as
   * "found it myself" (id "self"). Takes the place of the "?".
   */
  foundSelf?: boolean;
  /** Reserve the last inner-ring spot for a dashed "+" (add someone you brought in). */
  showAddSlot?: boolean;
  /**
   * The centre's family, drawn beside the centre. Anyone here who is also a
   * recruit is drawn only here; an ancestor stays in the chain.
   */
  centerFamilyIds?: string[];
  /** Node id → family id, for grouping family members on the rings. */
  familyOf?: Record<string, string | null | undefined>;
}

export type OrbitNodeKind =
  | 'center'
  | 'referrer'
  | 'ancestor'
  | 'slot'
  | 'outside'
  | 'self'
  | 'add'
  | 'family'
  | 'child'
  | 'grandchild'
  | 'overflow';

export interface PlacedNode {
  id: string;
  kind: OrbitNodeKind;
  x: number;
  y: number;
  r: number;
  /** Which ring a child, grandchild, chip or "+" is on. */
  ring?: 'inner' | 'outer';
  /** For grandchildren: the child they hang off. */
  parentId?: string;
  /** For overflow chips: how many people the chip stands for. */
  hiddenCount?: number;
}

export interface OrbitEdge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  kind: 'up' | 'down';
  dashed?: boolean;
}

/** Soft shapes drawn behind family members. */
export type OrbitBubble =
  /** The centre and its family; x, y is the top-left corner. */
  | { kind: 'pill'; x: number; y: number; w: number; h: number }
  /** Family neighbours on a ring: a thick round-capped arc, angles in radians, clockwise. */
  | {
      kind: 'arc';
      ring: 'inner' | 'outer';
      cx: number;
      cy: number;
      radius: number;
      startAngle: number;
      endAngle: number;
      thickness: number;
    };

export interface OrbitLayout {
  width: number;
  height: number;
  cx: number;
  cy: number;
  innerR: number;
  outerR: number;
  nodes: PlacedNode[];
  edges: OrbitEdge[];
  bubbles: OrbitBubble[];
}

export const NODE_R = {
  center: 28,
  referrer: 19,
  ancestor: 15,
  slot: 19,
  outside: 19,
  self: 19,
  add: 16,
  family: 16,
  child: 16,
  grandchild: 10,
} as const;

const PAD = 12;
/** Room under the bottom ring for the recruits' name labels. */
const LABEL_SPACE = 18;
const ANCESTOR_STEP = 50;
/** Minimum clear space between neighbouring nodes on a ring. */
const RING_GAP = 8;
/** Half-width of the empty wedge kept above you, in radians. */
const TOP_WEDGE = (28 * Math.PI) / 180;
/** Half-width of the wedges kept clear either side of the family pill. */
const SIDE_WEDGE = (48 * Math.PI) / 180;
/** Space between the centre and a family member, and the pill's padding. */
const PILL_GAP = 6;
const PILL_PAD = 6;
/** Extra room at the bottom of the pill for the family members' names. */
const PILL_LABEL = 16;
/** Family members shown in the pill before the rest become a "+N" chip. */
const MAX_PILL_FAMILY = 2;
/** Extra thickness of an arc bubble around the nodes it groups. */
const ARC_PAD = 8;

const INNER_FRACTION = 0.24;
/** The inner ring moves out a little to make room for the family pill. */
const INNER_FRACTION_WITH_FAMILY = 0.28;
const OUTER_FRACTION = 0.4;

/** Smallest angle between two nodes of radius `r` on a ring of radius `R`. */
const minAngle = (r: number, R: number) => 2 * Math.asin(Math.min(1, (2 * r + RING_GAP) / (2 * R)));

/** How many of `total` fit in `capacity` spots, and how many a chip must stand for. */
function fit(total: number, capacity: number): { shown: number; hidden: number } {
  if (total <= capacity) return { shown: total, hidden: 0 };
  const shown = Math.max(0, capacity - 1);
  return { shown, hidden: total - shown };
}

/**
 * Reorders ids so members of one family are adjacent, the family placed where
 * its first member was; everyone else keeps their order.
 */
function groupFamilies(ids: string[], familyOf: Record<string, string | null | undefined>): string[] {
  const out: string[] = [];
  const done = new Set<string>();
  for (const id of ids) {
    if (done.has(id)) continue;
    const fam = familyOf[id];
    const group = fam ? ids.filter((x) => familyOf[x] === fam) : [id];
    for (const g of group) {
      out.push(g);
      done.add(g);
    }
  }
  return out;
}

/**
 * The free part of a circle once wedges are cut out, walked clockwise from
 * `start`. `at(t)` turns a distance along the free arcs into an angle, so
 * spreading nodes evenly along t never lands one in a wedge.
 */
function freeArcs(start: number, wedges: { center: number; half: number }[]) {
  const TAU = 2 * Math.PI;
  const norm = (a: number) => ((a - start) % TAU + TAU) % TAU;
  const cuts = wedges
    .map((w) => ({ from: norm(w.center - w.half), to: norm(w.center - w.half) + 2 * w.half }))
    .sort((a, b) => a.from - b.from);
  const arcs: { from: number; to: number }[] = [];
  let pos = 0;
  for (const c of cuts) {
    if (c.from > pos) arcs.push({ from: pos, to: c.from });
    pos = Math.max(pos, c.to);
  }
  // A wedge straddling `start` spills past TAU; what's left is free.
  if (pos < TAU) arcs.push({ from: pos, to: TAU });
  const length = arcs.reduce((sum, a) => sum + (a.to - a.from), 0);
  const at = (t: number) => {
    let rest = Math.min(Math.max(t, 0), length);
    for (let i = 0; i < arcs.length; i++) {
      const span = arcs[i].to - arcs[i].from;
      if (rest <= span || i === arcs.length - 1) return { angle: start + arcs[i].from + rest, arc: i };
      rest -= span;
    }
    return { angle: start, arc: 0 };
  };
  return { length, at };
}

export function layoutOrbit(input: OrbitInput, width: number): OrbitLayout {
  const familyOf = input.familyOf ?? {};
  const ancestorSet = new Set(input.ancestorIds);
  const pillIds = (input.centerFamilyIds ?? []).filter((id) => !ancestorSet.has(id));
  const pillSet = new Set(pillIds);
  const hasPill = pillIds.length > 0;

  const innerR = width * (hasPill ? INNER_FRACTION_WITH_FAMILY : INNER_FRACTION);
  const outerR = width * OUTER_FRACTION;
  const cx = width / 2;

  const upward =
    input.ancestorIds.length > 0
      ? input.ancestorIds.length
      : input.outsideReferrer || input.foundSelf || input.showReferrerSlot
        ? 1
        : 0;
  const cy = upward > 0
    ? PAD + NODE_R.referrer + (upward - 1) * ANCESTOR_STEP + outerR
    : PAD + outerR + NODE_R.grandchild;
  const height = cy + outerR + NODE_R.grandchild + LABEL_SPACE + PAD;

  const nodes: PlacedNode[] = [{ id: 'center', kind: 'center', x: cx, y: cy, r: NODE_R.center }];
  const edges: OrbitEdge[] = [];
  const bubbles: OrbitBubble[] = [];

  // ── Up: the referrer chain, someone outside RHWB, "found it myself", or "?" ─
  if (input.ancestorIds.length > 0) {
    let prev = { x: cx, y: cy };
    input.ancestorIds.forEach((id, i) => {
      const y = cy - outerR - i * ANCESTOR_STEP;
      const kind = i === 0 ? 'referrer' : 'ancestor';
      nodes.push({ id, kind, x: cx, y, r: NODE_R[kind] });
      edges.push({ x1: prev.x, y1: prev.y, x2: cx, y2: y, kind: 'up' });
      prev = { x: cx, y };
    });
  } else if (input.outsideReferrer) {
    const y = cy - outerR;
    nodes.push({ id: 'outside', kind: 'outside', x: cx, y, r: NODE_R.outside });
    edges.push({ x1: cx, y1: cy, x2: cx, y2: y, kind: 'up', dashed: true });
  } else if (input.foundSelf) {
    // No line: nobody brought you in.
    nodes.push({ id: 'self', kind: 'self', x: cx, y: cy - outerR, r: NODE_R.self });
  } else if (input.showReferrerSlot) {
    const y = cy - outerR;
    nodes.push({ id: 'slot', kind: 'slot', x: cx, y, r: NODE_R.slot });
    edges.push({ x1: cx, y1: cy, x2: cx, y2: y, kind: 'up', dashed: true });
  }

  // ── Sideways: your family, in a pill around you ─────────────────────────
  if (hasPill) {
    const pill = fit(pillIds.length, MAX_PILL_FAMILY);
    const spots = pill.shown + (pill.hidden > 0 ? 1 : 0);
    const d = NODE_R.center + PILL_GAP + NODE_R.family;
    // Right first, then left.
    const xs = [cx + d, cx - d].slice(0, spots);
    xs.forEach((x, i) => {
      if (i >= pill.shown) {
        nodes.push({ id: 'overflow-family', kind: 'overflow', x, y: cy, r: NODE_R.family, hiddenCount: pill.hidden });
      } else {
        nodes.push({ id: pillIds[i], kind: 'family', x, y: cy, r: NODE_R.family });
      }
    });
    const left = Math.min(cx - NODE_R.center, ...xs.map((x) => x - NODE_R.family)) - PILL_PAD;
    const right = Math.max(cx + NODE_R.center, ...xs.map((x) => x + NODE_R.family)) + PILL_PAD;
    const top = cy - NODE_R.center - PILL_PAD;
    const h = 2 * (NODE_R.center + PILL_PAD) + PILL_LABEL;
    bubbles.push({ kind: 'pill', x: left, y: top, w: right - left, h });
  }

  // ── Down: inner ring ────────────────────────────────────────────────────
  const wedges: { center: number; half: number }[] = [];
  if (upward > 0) wedges.push({ center: -Math.PI / 2, half: TOP_WEDGE });
  if (hasPill) wedges.push({ center: 0, half: SIDE_WEDGE }, { center: Math.PI, half: SIDE_WEDGE });
  const start = upward > 0 ? -Math.PI / 2 + TOP_WEDGE : -Math.PI / 2;
  const ring = freeArcs(start, wedges);

  const childIds = groupFamilies(input.childIds.filter((id) => !pillSet.has(id)), familyOf);
  const addSlots = input.showAddSlot ? 1 : 0;
  const innerCap = Math.max(1, Math.floor(ring.length / minAngle(NODE_R.child, innerR)) - addSlots);
  const inner = fit(childIds.length, innerCap);
  const slots = inner.shown + (inner.hidden > 0 ? 1 : 0) + addSlots;
  if (slots === 0) return { width, height, cx, cy, innerR, outerR, nodes, edges, bubbles };

  const share = ring.length / slots;
  const point = (angle: number, R: number) => ({ x: cx + R * Math.cos(angle), y: cy + R * Math.sin(angle) });

  const gcByParent = new Map<string, string[]>();
  for (const g of input.grandchildren) {
    if (!gcByParent.has(g.parentId)) gcByParent.set(g.parentId, []);
    gcByParent.get(g.parentId)!.push(g.id);
  }
  const outerCap = Math.max(1, Math.floor(share / minAngle(NODE_R.grandchild, outerR)));

  /** Arc bubbles over each run of 2+ same-family neighbours on one free arc. */
  const addFamilyArcs = (
    placed: { id: string; angle: number; arc: number }[],
    R: number,
    r: number,
    ringName: 'inner' | 'outer'
  ) => {
    let i = 0;
    while (i < placed.length) {
      const fam = familyOf[placed[i].id];
      let j = i;
      while (j + 1 < placed.length && fam && familyOf[placed[j + 1].id] === fam && placed[j + 1].arc === placed[i].arc) j++;
      if (fam && j > i) {
        bubbles.push({
          kind: 'arc',
          ring: ringName,
          cx,
          cy,
          radius: R,
          startAngle: placed[i].angle,
          endAngle: placed[j].angle,
          thickness: 2 * r + ARC_PAD,
        });
      }
      i = j + 1;
    }
  };

  const innerPlaced: { id: string; angle: number; arc: number }[] = [];
  for (let i = 0; i < slots; i++) {
    const { angle, arc } = ring.at((i + 0.5) * share);
    const p = point(angle, innerR);

    if (addSlots && i === slots - 1) {
      nodes.push({ id: 'add', kind: 'add', ring: 'inner', ...p, r: NODE_R.add });
      edges.push({ x1: cx, y1: cy, x2: p.x, y2: p.y, kind: 'down', dashed: true });
      continue;
    }
    if (i >= inner.shown) {
      nodes.push({ id: 'overflow-inner', kind: 'overflow', ring: 'inner', ...p, r: NODE_R.child, hiddenCount: inner.hidden });
      edges.push({ x1: cx, y1: cy, x2: p.x, y2: p.y, kind: 'down', dashed: true });
      continue;
    }

    const id = childIds[i];
    nodes.push({ id, kind: 'child', ring: 'inner', ...p, r: NODE_R.child });
    edges.push({ x1: cx, y1: cy, x2: p.x, y2: p.y, kind: 'down' });
    innerPlaced.push({ id, angle, arc });

    // ── Down again: this child's share of the outer ring ──────────────────
    const kids = groupFamilies(gcByParent.get(id) ?? [], familyOf);
    const outer = fit(kids.length, outerCap);
    const outerSlots = outer.shown + (outer.hidden > 0 ? 1 : 0);
    const outerPlaced: { id: string; angle: number; arc: number }[] = [];
    for (let j = 0; j < outerSlots; j++) {
      const q = ring.at(i * share + ((j + 0.5) * share) / outerSlots);
      const qp = point(q.angle, outerR);
      if (j >= outer.shown) {
        nodes.push({ id: `overflow-${id}`, kind: 'overflow', ring: 'outer', ...qp, r: NODE_R.grandchild, parentId: id, hiddenCount: outer.hidden });
        edges.push({ x1: p.x, y1: p.y, x2: qp.x, y2: qp.y, kind: 'down', dashed: true });
      } else {
        nodes.push({ id: kids[j], kind: 'grandchild', ring: 'outer', ...qp, r: NODE_R.grandchild, parentId: id });
        edges.push({ x1: p.x, y1: p.y, x2: qp.x, y2: qp.y, kind: 'down' });
        outerPlaced.push({ id: kids[j], angle: q.angle, arc: q.arc });
      }
    }
    addFamilyArcs(outerPlaced, outerR, NODE_R.grandchild, 'outer');
  }
  addFamilyArcs(innerPlaced, innerR, NODE_R.child, 'inner');

  // Renderers draw every bubble before edges and nodes, so they sit underneath.
  return { width, height, cx, cy, innerR, outerR, nodes, edges, bubbles };
}

/**
 * The layout input for a view, shared so the mobile and web Orbits group the
 * same people the same way.
 */
export function orbitInputFromView(
  view: NetworkView,
  opts: { showReferrerSlot?: boolean; showAddSlot?: boolean } = {}
): OrbitInput {
  const familyOf: Record<string, string | null> = {};
  for (const n of [view.me, ...view.ancestors, ...view.children, ...view.grandchildren, ...(view.family ?? [])]) {
    if (n) familyOf[n.id] = n.familyId ?? null;
  }
  return {
    ancestorIds: view.ancestors.map((n) => n.id),
    childIds: view.children.map((n) => n.id),
    grandchildren: view.grandchildren.map((g) => ({ id: g.id, parentId: g.parentId })),
    showReferrerSlot: !!opts.showReferrerSlot && view.ancestors.length === 0,
    outsideReferrer: !!view.outsideReferrer && view.ancestors.length === 0,
    foundSelf: !!view.foundSelf && !view.outsideReferrer && view.ancestors.length === 0,
    showAddSlot: !!opts.showAddSlot,
    centerFamilyIds: (view.family ?? []).map((n) => n.id),
    familyOf,
  };
}

/** Whether a view's diagram will draw any family bubble, so a legend is worth showing. */
export function hasFamilyBubbles(view: NetworkView): boolean {
  if ((view.family ?? []).length > 0) return true;
  const seen = new Set<string>();
  for (const n of [...view.children, ...view.grandchildren]) {
    if (!n.familyId) continue;
    if (seen.has(n.familyId)) return true;
    seen.add(n.familyId);
  }
  return false;
}

/** Every runner a view names, by id — for looking a placed node back up. */
export function nodesById(view: NetworkView): Map<string, NetworkNode> {
  const m = new Map<string, NetworkNode>();
  for (const n of [...view.ancestors, ...view.children, ...view.grandchildren, ...(view.family ?? [])]) m.set(n.id, n);
  return m;
}

/** SVG path for an arc bubble, drawn with stroke-width = thickness and round caps. */
export function arcPath(b: Extract<OrbitBubble, { kind: 'arc' }>): string {
  const x1 = b.cx + b.radius * Math.cos(b.startAngle);
  const y1 = b.cy + b.radius * Math.sin(b.startAngle);
  const x2 = b.cx + b.radius * Math.cos(b.endAngle);
  const y2 = b.cy + b.radius * Math.sin(b.endAngle);
  const large = b.endAngle - b.startAngle > Math.PI ? 1 : 0;
  const f = (n: number) => String(Math.round(n * 100) / 100);
  return `M ${f(x1)} ${f(y1)} A ${f(b.radius)} ${f(b.radius)} 0 ${large} 1 ${f(x2)} ${f(y2)}`;
}
